import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { liveBroadcasts, type LiveBroadcast, type LiveStatus } from "@/mocks/lives";
import Swal from "sweetalert2";

type FilterTab = "all" | LiveStatus;

const FILTER_TABS: { key: FilterTab; label: string; icon: string; count: number }[] = [
  { key: "all", label: "Todos", icon: "ri-list-check", count: liveBroadcasts.length },
  { key: "live", label: "En vivo", icon: "ri-live-line", count: liveBroadcasts.filter((l) => l.status === "live").length },
  { key: "scheduled", label: "Próximos", icon: "ri-calendar-line", count: liveBroadcasts.filter((l) => l.status === "scheduled").length },
  { key: "finished", label: "Finalizados", icon: "ri-check-double-line", count: liveBroadcasts.filter((l) => l.status === "finished").length },
];

const INITIAL_FOLLOWED = new Set([16, 3, 14, 19]);
const INITIAL_REMINDERS = new Set<number>([8, 9, 12]);

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

function fmtDateLong(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
}

export default function AppLivePage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterTab>("all");
  const [followed, setFollowed] = useState<Set<number>>(() => new Set(INITIAL_FOLLOWED));
  const [reminders, setReminders] = useState<Set<number>>(() => new Set(INITIAL_REMINDERS));

  const liveList = useMemo(() => liveBroadcasts.filter((l) => l.status === "live"), []);
  const scheduledList = useMemo(() => liveBroadcasts.filter((l) => l.status === "scheduled"), []);
  const finishedList = useMemo(() => liveBroadcasts.filter((l) => l.status === "finished"), []);

  const filtered = useMemo(() => {
    if (filter === "all") return [...liveList, ...scheduledList, ...finishedList];
    if (filter === "live") return liveList;
    if (filter === "scheduled") return scheduledList;
    return finishedList;
  }, [filter, liveList, scheduledList, finishedList]);

  /* ─── Ver live ─── */
  const handleViewLive = useCallback((e: React.MouseEvent, live: LiveBroadcast) => {
    e.stopPropagation();
    Swal.fire({
      html: `
        <div class="flex flex-col items-center text-center">
          <div class="relative w-full h-40 rounded-xl overflow-hidden mb-4">
            <img src="${live.coverUrl}" alt="${live.programName}" class="w-full h-full object-cover object-top" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div class="absolute bottom-3 left-3 flex items-center gap-2">
              <span class="relative flex h-2.5 w-2.5">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
              </span>
              <span class="text-white text-xs font-extrabold tracking-wider">EN VIVO</span>
            </div>
            <div class="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/50 text-white text-[10px] font-bold backdrop-blur-sm">
              ${live.station.listeners >= 1000 ? (live.station.listeners / 1000).toFixed(1) + 'K' : live.station.listeners} escuchando
            </div>
          </div>
          <div class="w-14 h-14 rounded-xl overflow-hidden border-2 border-background-200 mb-2">
            <img src="${live.station.logoUrl}" alt="${live.station.name}" class="w-full h-full object-cover" />
          </div>
          <h2 class="text-foreground-950 font-bold text-base">${live.programName}</h2>
          <p class="text-foreground-500 text-sm mt-0.5">${live.hostName}</p>
          <p class="text-foreground-600 text-xs mt-1 font-medium">${live.station.name} · ${live.station.frequency} WEB</p>
          <div class="flex items-center gap-3 mt-3 text-xs text-foreground-400">
            <span><i class="ri-time-line mr-1"></i>${fmtTime(live.scheduledTime)} - ${fmtTime(live.endTime)}</span>
            <span>${live.durationMinutes} min</span>
          </div>
          <p class="text-foreground-500 text-xs mt-3 leading-relaxed px-2">${live.description}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Ir a la radio",
      cancelButtonText: "Cerrar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#e2e8f0",
      customClass: { popup: "rounded-2xl max-w-md" },
      didOpen: () => {
        const confirmBtn = Swal.getConfirmButton();
        if (confirmBtn) confirmBtn.addEventListener("click", () => {
          navigate(`/radio/${live.station.frequency.replace(".", "-")}`);
        });
      },
    });
  }, [navigate]);

  /* ─── Seguir radio ─── */
  const handleFollow = useCallback((e: React.MouseEvent, live: LiveBroadcast) => {
    e.stopPropagation();
    const sid = live.station.id;
    setFollowed((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) {
        next.delete(sid);
        Swal.fire({ toast: true, position: "top-end", icon: "info", title: `Dejaste de seguir a ${live.station.name}`, showConfirmButton: false, timer: 1800, timerProgressBar: true });
      } else {
        next.add(sid);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Ahora sigues a ${live.station.name}`, showConfirmButton: false, timer: 1800, timerProgressBar: true });
      }
      return next;
    });
  }, []);

  /* ─── Compartir ─── */
  const handleShare = useCallback(async (e: React.MouseEvent, live: LiveBroadcast) => {
    e.stopPropagation();
    const url = `${window.location.origin}/radio/${live.station.frequency.replace(".", "-")}`;
    const text = `${live.programName} · ${live.station.name} ${live.station.frequency} WEB — RadioFrecuenciaWeb`;
    if (navigator.share) {
      try { await navigator.share({ title: live.programName, text, url }); } catch { /* cancel */ }
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Enlace copiado al portapapeles", showConfirmButton: false, timer: 1500, timerProgressBar: true });
    }
  }, []);

  /* ─── Recordarme ─── */
  const handleRemind = useCallback((e: React.MouseEvent, live: LiveBroadcast) => {
    e.stopPropagation();
    setReminders((prev) => {
      const next = new Set(prev);
      if (next.has(live.id)) {
        next.delete(live.id);
        Swal.fire({ toast: true, position: "top-end", icon: "info", title: "Recordatorio cancelado", showConfirmButton: false, timer: 1800, timerProgressBar: true });
      } else {
        next.add(live.id);
        Swal.fire({
          icon: "success",
          title: "Recordatorio activado",
          html: `Te avisaremos cuando <strong>${live.programName}</strong> esté por comenzar.<br><br><span class="text-foreground-500 text-sm">${fmtDateLong(live.scheduledTime)} · ${fmtTime(live.scheduledTime)}</span>`,
          confirmButtonText: "Perfecto",
          confirmButtonColor: "#f97316",
          customClass: { popup: "rounded-2xl" },
        });
      }
      return next;
    });
  }, []);

  /* ─── Ver finalizado ─── */
  const handleFinished = useCallback((e: React.MouseEvent, live: LiveBroadcast) => {
    e.stopPropagation();
    Swal.fire({
      title: live.programName,
      html: `
        <div class="text-left space-y-3">
          <div class="flex items-center gap-2">
            <img src="${live.station.logoUrl}" alt="${live.station.name}" class="w-10 h-10 rounded-lg object-cover" />
            <span class="font-semibold text-foreground-950">${live.station.name} · ${live.station.frequency} WEB</span>
          </div>
          <p class="text-sm text-foreground-500"><i class="ri-user-line mr-1"></i>${live.hostName}</p>
          <p class="text-sm text-foreground-500"><i class="ri-time-line mr-1"></i>${fmtDateLong(live.scheduledTime)} · ${fmtTime(live.scheduledTime)} - ${fmtTime(live.endTime)} (${live.durationMinutes} min)</p>
          <p class="text-sm text-foreground-600 leading-relaxed">${live.description}</p>
          <div class="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-foreground-100 text-foreground-500 border border-foreground-200">
            <i class="ri-check-double-line mr-1"></i>Finalizado
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Ver radio",
      cancelButtonText: "Cerrar",
      confirmButtonColor: "#0f172a",
      cancelButtonColor: "#e2e8f0",
      customClass: { popup: "rounded-2xl" },
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/radio/${live.station.frequency.replace(".", "-")}`);
      }
    });
  }, [navigate]);

  /* ─── Sección helper ─── */
  const renderSection = (
    title: string,
    icon: string,
    iconBg: string,
    iconColor: string,
    items: LiveBroadcast[],
    accentBorder: string,
  ) => (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center`}>
            <i className={`${icon} ${iconColor} text-sm`}></i>
          </div>
          <h2 className="text-foreground-950 font-bold text-sm">{title}</h2>
        </div>
        <span className="text-foreground-400 text-[11px] font-medium">{items.length}</span>
      </div>
      <div className={`space-y-3 ${accentBorder}`}>
        {items.map((live) => (
          <LiveCard
            key={live.id}
            live={live}
            isFollowed={followed.has(live.station.id)}
            hasReminder={reminders.has(live.id)}
            onViewLive={handleViewLive}
            onFollow={handleFollow}
            onShare={handleShare}
            onRemind={handleRemind}
            onFinished={handleFinished}
            onNavigateStation={(e) => {
              e.stopPropagation();
              navigate(`/radio/${live.station.frequency.replace(".", "-")}`);
            }}
          />
        ))}
      </div>
    </section>
  );

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200 safe-top">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer shrink-0 transition-all"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2 flex-1">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
              <i className="ri-live-fill text-red-500 text-lg"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-lg">Live Discovery</h1>
              <p className="text-foreground-400 text-[11px]">
                {liveList.length} en vivo · {scheduledList.length} próximos · {finishedList.length} finalizados
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 pb-20">
        {/* Filter Tabs */}
        <div className="sticky top-[57px] z-20 bg-background-50/95 backdrop-blur-sm pt-4 pb-3">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-1">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  filter === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200"
                }`}
              >
                <i className={`${tab.icon} text-xs`}></i>
                {tab.label}
                <span className="text-[10px] opacity-80">({tab.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mt-1">
          {filter === "all" ? (
            <>
              {renderSection("En vivo ahora", "ri-live-fill", "bg-red-50", "text-red-500", liveList, "")}
              {renderSection("Próximos lives", "ri-calendar-event-line", "bg-amber-50", "text-amber-500", scheduledList, "")}
              {renderSection("Finalizados", "ri-check-double-line", "bg-foreground-100", "text-foreground-500", finishedList, "")}
            </>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-search-line text-foreground-300 text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm font-medium">Sin resultados</p>
              <p className="text-foreground-400 text-xs mt-1">No hay programas en esta categoría ahora</p>
              <button
                onClick={() => setFilter("all")}
                className="mt-4 px-4 py-2 bg-background-100 hover:bg-background-200 text-foreground-600 text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all"
              >
                Mostrar todos
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((live) => (
                <LiveCard
                  key={live.id}
                  live={live}
                  isFollowed={followed.has(live.station.id)}
                  hasReminder={reminders.has(live.id)}
                  onViewLive={handleViewLive}
                  onFollow={handleFollow}
                  onShare={handleShare}
                  onRemind={handleRemind}
                  onFinished={handleFinished}
                  onNavigateStation={(e) => {
                    e.stopPropagation();
                    navigate(`/radio/${live.station.frequency.replace(".", "-")}`);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ─── LiveCard ─── */
function LiveCard({
  live,
  isFollowed,
  hasReminder,
  onViewLive,
  onFollow,
  onShare,
  onRemind,
  onFinished,
  onNavigateStation,
}: {
  live: LiveBroadcast;
  isFollowed: boolean;
  hasReminder: boolean;
  onViewLive: (e: React.MouseEvent, live: LiveBroadcast) => void;
  onFollow: (e: React.MouseEvent, live: LiveBroadcast) => void;
  onShare: (e: React.MouseEvent, live: LiveBroadcast) => void;
  onRemind: (e: React.MouseEvent, live: LiveBroadcast) => void;
  onFinished: (e: React.MouseEvent, live: LiveBroadcast) => void;
  onNavigateStation: (e: React.MouseEvent) => void;
}) {
  const isLive = live.status === "live";
  const isScheduled = live.status === "scheduled";
  const isFinished = live.status === "finished";

  const borderClass = isLive
    ? "border-red-200 hover:border-red-300"
    : isScheduled
    ? "border-amber-200 hover:border-amber-300"
    : "border-background-200 hover:border-foreground-300";

  return (
    <div
      onClick={(e) => {
        if (isFinished) { onFinished(e, live); return; }
        onViewLive(e, live);
      }}
      className={`group bg-background-50 border rounded-2xl p-4 transition-all duration-200 cursor-pointer relative ${borderClass} ${isFinished ? "opacity-75 hover:opacity-90" : ""}`}
    >
      {/* Top accent bar for live */}
      {isLive && (
        <div className="absolute top-0 left-4 right-4 h-0.5 bg-gradient-to-r from-red-500 via-red-400 to-red-500 rounded-full"></div>
      )}

      <div className={isLive ? "pt-1" : ""}>
        {/* Cover + Info */}
        <div className="flex items-start gap-4">
          {/* Cover image */}
          <div className="relative w-32 h-20 shrink-0 rounded-xl overflow-hidden bg-background-100 border border-background-200">
            <img src={live.coverUrl} alt={live.programName} className="w-full h-full object-cover object-top" loading="lazy" />
            {/* Status badge */}
            <div className={`absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 border ${
              isLive
                ? "bg-red-50 text-red-600 border-red-200"
                : isScheduled
                ? "bg-amber-50 text-amber-600 border-amber-200"
                : "bg-foreground-100 text-foreground-500 border-foreground-200"
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-red-500 animate-pulse" : isScheduled ? "bg-amber-500" : "bg-foreground-400"}`}></span>
              {isLive ? "EN VIVO" : isScheduled ? "Próximo" : "Finalizado"}
            </div>
          </div>

          {/* Info text */}
          <div className="min-w-0 flex-1">
            <h3 className="text-foreground-950 font-bold text-sm truncate">{live.programName}</h3>
            <p className="text-foreground-600 text-xs mt-0.5 font-medium truncate">{live.hostName}</p>

            {/* Station */}
            <div className="flex items-center gap-2 mt-1.5">
              <div className="w-5 h-5 rounded-md overflow-hidden bg-background-100 border border-background-200 shrink-0">
                <img src={live.station.logoUrl} alt={live.station.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <span className="text-foreground-500 text-[11px] truncate">
                {live.station.name} · {live.station.frequency} WEB
              </span>
            </div>

            {/* Time row */}
            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-foreground-400">
              <span><i className="ri-time-line mr-1"></i>{fmtTime(live.scheduledTime)} - {fmtTime(live.endTime)}</span>
              <span>{live.durationMinutes} min</span>
              <span className="hidden sm:inline"><i className="ri-calendar-line mr-1"></i>{fmtDate(live.scheduledTime)}</span>
            </div>
          </div>

          {/* Action buttons column */}
          <div className="flex flex-col items-center gap-1 shrink-0">
            {/* Main CTA: Ver live / Recordarme / Ver resumen */}
            {isLive && (
              <button
                onClick={(e) => onViewLive(e, live)}
                className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer bg-red-500 text-white hover:bg-red-600 transition-all"
                title="Ver live"
              >
                <i className="ri-eye-fill text-sm"></i>
              </button>
            )}
            {isScheduled && (
              <button
                onClick={(e) => onRemind(e, live)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all ${
                  hasReminder
                    ? "bg-amber-100 text-amber-500 hover:bg-amber-200"
                    : "bg-amber-500 text-white hover:bg-amber-600"
                }`}
                title={hasReminder ? "Cancelar recordatorio" : "Recordarme"}
              >
                <i className={`text-sm ${hasReminder ? "ri-alarm-fill" : "ri-alarm-line"}`}></i>
              </button>
            )}
            {isFinished && (
              <button
                onClick={(e) => onFinished(e, live)}
                className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer bg-foreground-100 text-foreground-500 hover:bg-foreground-200 hover:text-foreground-700 transition-all"
                title="Ver resumen"
              >
                <i className="ri-information-line text-sm"></i>
              </button>
            )}

            {/* Follow */}
            <button
              onClick={(e) => onFollow(e, live)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all ${
                isFollowed
                  ? "bg-accent-100 text-accent-500 hover:bg-accent-200"
                  : "bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-accent-500"
              }`}
              title={isFollowed ? "Dejar de seguir" : "Seguir radio"}
            >
              <i className={`text-sm ${isFollowed ? "ri-user-follow-fill" : "ri-user-follow-line"}`}></i>
            </button>

            {/* Share */}
            <button
              onClick={(e) => onShare(e, live)}
              className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all"
              title="Compartir"
            >
              <i className="ri-share-forward-line text-sm"></i>
            </button>
          </div>
        </div>

        {/* Description (truncated) */}
        <div className="mt-2 pt-2 border-t border-background-100">
          <p className="text-foreground-500 text-[11px] leading-relaxed line-clamp-1">
            {live.description}
          </p>
        </div>
      </div>
    </div>
  );
}